import React, { useState, useEffect } from "react";

import { ShipSheet } from "../summary/ShipSheet";
import { ShipWarnings } from "../summary/ShipWarnings";

/**
 * Step 13: Finalise Design
 * Tabbed interface allowing users to:
 * - View ship warnings and validation issues
 * - Review the final ship summary
 */
export const FinaliseDesignStep: React.FC = () => {
  // Get the last opened tab from localStorage, default to "warnings"
  const getInitialTab = (): "warnings" | "summary" => {
    const stored = localStorage.getItem('finaliseDesignLastTab');
    if (stored && ['warnings', 'summary'].includes(stored)) {
      return stored as "warnings" | "summary";
    }
    return "warnings"; // Default to Ship Warnings tab
  };

  const [activeTab, setActiveTab] = useState<
    "warnings" | "summary"
  >(getInitialTab);

  // Save the active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('finaliseDesignLastTab', activeTab);
  }, [activeTab]);

  const tabs = [
    { id: "warnings", label: "Ship Warnings", icon: "⚠️" },
    { id: "summary", label: "Ship Summary", icon: "📋" },
  ] as const;

  return (
    <div className="space-y-6 pb-32">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Finalise Design
        </h2>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-600 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "warnings" && (
            <div className="warnings-tab">
              <ShipWarnings />
            </div>
          )}

          {activeTab === "summary" && (
            <div className="summary-tab">
              <ShipSheet />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
